import { z, type ZodRawShape } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { jsonBody, privateApi, successResponse, tagPaths } from "../util.ts";
import {
    billing_account_id,
    created_at,
    datetime_z,
    deleted_at,
    ipv4,
    network_uuid,
    replica_uuid,
    simpleSuccessResponse,
    updated_at,
    user_id,
    uuid,
} from "./common.ts";
import * as params from "./params.ts";
import { disk_size_gb, memory, ReplicaType, vcpu, VmStatusEnum } from "./vm.ts";

extendZodWithOpenApi(z);

const ManagedServicePriceTypeEnum = z
    .enum([
        "CPU",
        "RAM",
        // Only used for price multiplier for main BLOCK_STORAGE
        "STORAGE",
        "OBJECT_STORAGE",
        "BLOCK_STORAGE",
        "BLOCK_STORAGE_SNAPSHOT",
        "BLOCK_STORAGE_BACKUP",
        "WINDOWS_LICENSE",
        "LICENSE",
        "UNASSIGNED_FLOATING_IP",
        "ASSIGNED_FLOATING_IP",
        "LOAD_BALANCER",
        // XXX: Should `BARE_METAL_SERVER` be here?
    ])
    .describe("Managed service price resource type")
    .openapi({ example: "CPU", ref: "ManagedServicePriceTypeEnum" });

const UptimeName = z.enum(["main", "block", "snapshot", "backup", "windows"]).openapi({ ref: "UptimeName" });

const CamelCasePrice = z
    .object({
        priceMultiplier: z.number(),
        resourceType: ManagedServicePriceTypeEnum,
        serviceNameInUptime: UptimeName.optional(),
    })
    .openapi({ ref: "CamelCasePrice" });

const SnakeCasePrice = z
    .object({
        price_multiplier: z.number(),
        resource_type: ManagedServicePriceTypeEnum,
    })
    .openapi({ ref: "SnakeCasePrice" });

/**
 * NB: Prices are duplicated in two different formats
 * See:
 *      https://oye.slack.com/archives/G012FAQL06S/p1670247135865529
 *      https://warrenio.atlassian.net/browse/WAR-4166
 */
export const ManagedServicesPricesBody = z
    .array(z.union([CamelCasePrice, SnakeCasePrice]))
    .openapi({ ref: "ManagedServicesPricesBody" });

export const ManagedServicesVmStorageReplica = z
    .object({
        uuid,
        created_at,
        size: disk_size_gb,
        type: ReplicaType,
    })
    .openapi({ ref: "ManagedServicesVmStorageReplica" });

export const ManagedServicesVmStorage = z
    .object({
        uuid,
        primary: z.boolean(),
        size: disk_size_gb,
        replica: z.array(ManagedServicesVmStorageReplica),
    })
    .openapi({ ref: "ManagedServicesVmStorage" });

const ManagedServiceResourceType = z
    .enum(["vm", "virtual_ip", "network", "kong_token"])
    .openapi({ example: "vm", ref: "ManagedServiceResourceType" });

export const ManagedServicesVmBody = z
    .object({
        resource_allocation: z.object({
            address: ipv4.optional(),
            memory: memory.optional(),
            vcpu: vcpu.optional(),
            status: VmStatusEnum.optional(),
            storage: z.array(ManagedServicesVmStorage).optional(),
        }),
        resource_id: z.string(),
        resource_location: z.string().optional(),
        resource_type: ManagedServiceResourceType,
    })
    .openapi({ ref: "ManagedServicesVmBody" });

/**
 * https://gitlab.com/warrenio/platform/framework/user-resource-interface/blob/af089adac60c1a4aaf0cc9e417a8ece54a747622/api/resources/service/package_setup.py#L33-41
 * https://gitlab.com/warrenio/platform/framework/user-resource-interface/blob/11bf6cd704c279de1ffb43941a741ffad0ddb383/api/resources/service_package_resources.py#L552-605
 */
export const ManagedServiceEnum = z
    .enum(["mariadb", "postgresql", "kubernetes", "self-service-dummy-testing"])
    .openapi({ example: "mariadb", ref: "ManagedServiceEnum" });

const ServicePropertiesBase = z
    .object({
        location: z.string(),
    })
    .openapi({ ref: "ServicePropertiesBase" });

export const KubernetesProviderWorker = z
    .object({
        cri: z.object({ name: z.string() }).optional(),
        machine: z.object({
            architecture: z.string().optional(),
            image: z.object({ name: z.string(), version: z.string() }).optional(),
            ram: z.number(),
            vcpu: z.number(),
        }),
        maxSurge: z.number(),
        maxUnavailable: z.number().optional(),
        maximum: z.number(),
        minimum: z.number(),
        name: z.string(),
        systemComponents: z.object({ allow: z.boolean() }).optional(),
        volume: z.object({ size: z.string() }),
        zones: z.array(z.string()).optional(),
    })
    .openapi({ ref: "KubernetesProviderWorker" });

export const KubernetesMaintenance = z
    .object({
        autoUpdate: z.object({
            kubernetesVersion: z.boolean(),
            machineImageVersion: z.boolean(),
        }),
        timeWindow: z.object({
            begin: z.string(),
            end: z.string(),
        }),
    })
    .openapi({ ref: "KubernetesMaintenance" });

export const KubernetesReadynessTypeEnum = z
    .enum([
        "APIServerAvailable",
        "EveryNodeReady",
        "SystemComponentsHealthy",
        "ControlPlaneHealthy",
        "ObservabilityComponentsHealthy",
    ])
    .openapi({ ref: "KubernetesReadynessTypeEnum" });

export const KubernetesStatusConditions = z
    .object({
        lastTransitionTime: datetime_z,
        lastUpdateTime: datetime_z,
        message: z.string(),
        reason: z.string(),
        status: z.string(),
        type: KubernetesReadynessTypeEnum,
    })
    .openapi({ ref: "KubernetesStatusConditions" });

export const KubernetesStatusLastOperation = z
    .object({
        description: z.string(),
        lastUpdateTime: datetime_z,
        progress: z.number(),
        state: z.string(),
        type: z.string(),
    })
    .openapi({ ref: "KubernetesStatusLastOperation" });

export const KubernetesPropertiesBody = z
    .object({
        maintenance: KubernetesMaintenance,
        provider: z.object({ workers: z.array(KubernetesProviderWorker) }),
    })
    .openapi({ ref: "KubernetesPropertiesBody" });

export const KubernetesHibernationBody = z
    .object({
        hibernation: z.boolean(),
    })
    .openapi({ ref: "KubernetesHibernationBody" });

const KubernetesSpec = z
    .object({
        hibernation: z.object({
            enabled: z.boolean(),
        }),
        kubernetes: z.any(),
        maintenance: KubernetesMaintenance,
        networking: z.any(),
        provider: z.object({
            type: z.string(),
            controlPlaneConfig: z.any(),
            infrastructureConfig: z.any(),
            workers: z.array(KubernetesProviderWorker),
            workersSettings: z.any(),
        }),
    })
    .openapi({ ref: "KubernetesSpec" });

const KubernetesStatus = z
    .object({
        hibernated: z.boolean(),
        conditions: z.array(KubernetesStatusConditions),
        constraints: z.any(),
        gardener: z.any(),
        lastOperation: KubernetesStatusLastOperation.optional(),
        observedGeneration: z.any(),
        retryCycleStartTime: z.any(),
        seedName: z.any(),
        technicalID: z.any(),
        uid: z.any(),
        clusterIdentity: z.any(),
        advertisedAddresses: z.any(),
        lastErrors: z.any(),
        lastMaintenance: z.any(),
    })
    .openapi({ ref: "KubernetesStatus" });

const KubernetesProperties = ServicePropertiesBase.extend({
    cluster_name: z.string(),
    spec: KubernetesSpec.optional(),
    status: KubernetesStatus.optional(),
}).openapi({ ref: "KubernetesProperties" });

const DbServiceProperties = ServicePropertiesBase.extend({
    service_ip: ipv4,
    sql_user: z.string(),
    port: z.number(),
    // Legacy?
    network_uuid: z.string().nullable().optional().openapi({ deprecated: true }),
}).openapi({ ref: "DbServiceProperties" });

const IpWithSubnet = z.string().openapi({ example: "10.15.198.0/24" });

const ServiceStatusEnum = z.enum(["active", "requested", "deleted"]).describe("Service state").openapi({
    example: "active",
    ref: "ServiceStatusEnum",
});

const ManagedServiceBaseFields = {
    billing_account_id,
    created_at,
    deleted_at,
    display_name: z.string(),
    is_deleted: z.boolean(),
    is_multi_node: z.boolean(),
    prices: ManagedServicesPricesBody,
    resources: z.array(ManagedServicesVmBody),
    status: ServiceStatusEnum,
    updated_at: updated_at.optional(),
    user_id,
    uuid,
    version: z.string(),
    // NB: Admin only, but put it here to simplify typing
    whitelist_addresses: z.array(IpWithSubnet).optional(),
} as const satisfies ZodRawShape;

export const ManagedServiceBase = z.object(ManagedServiceBaseFields).openapi({ ref: "ManagedServiceBase" });

const ManagedMariadbService = ManagedServiceBase.extend({
    service: z.literal("mariadb"),
    properties: DbServiceProperties,
}).openapi({ ref: "ManagedMariadbService" });

const ManagedPostgresqlService = ManagedServiceBase.extend({
    service: z.literal("postgresql"),
    properties: DbServiceProperties,
}).openapi({ ref: "ManagedPostgresqlService" });

const ManagedDbService = z
    .union([ManagedMariadbService, ManagedPostgresqlService])
    .openapi({ ref: "ManagedDbService" });

const ManagedKubernetesService = ManagedServiceBase.extend({
    service: z.literal("kubernetes"),
    properties: KubernetesProperties,
}).openapi({ ref: "ManagedKubernetesService" });

const _ManagedDummyService = ManagedServiceBase.extend({
    service: z.literal("self-service-dummy-testing"),
    properties: ServicePropertiesBase,
}).openapi({ ref: "ManagedDummyService" });

export const ManagedService = z
    .discriminatedUnion("service", [
        ManagedMariadbService,
        ManagedPostgresqlService,
        ManagedKubernetesService,
        _ManagedDummyService,
    ])
    .openapi({ ref: "ManagedService" });

export const ManagedServiceCreateBody = z
    .object({
        billing_account_id,
        display_name: ManagedServiceBaseFields.display_name,
        is_multi_node: ManagedServiceBaseFields.is_multi_node,
        package_parameters: z.object({
            location: z.string(),
            network_uuid: network_uuid.nullable().optional(),
            spec: z
                .object({
                    maintenance: KubernetesMaintenance,
                    provider: z.object({
                        workers: z.array(KubernetesProviderWorker),
                    }),
                })
                .optional(),
        }),
        reserve_public_ip: z.coerce.boolean().optional().default(true).describe("Reserve a public IP address"),
        service: ManagedServiceEnum,
        version: ManagedServiceBaseFields.version,
        vm_cpu: vcpu,
        vm_disk_gb: disk_size_gb,
        vm_ram: memory,
    })
    .openapi({ ref: "ManagedServiceCreateBody" });

export const ServiceListResponse = z.array(ManagedService);

export const ServiceImage = z
    .object({
        created_at,
        description: z.string(),
        display_name: z.string().describe("Display name"),
        icon: z.string().describe("base64-encoded icon").openapi({ contentEncoding: "base64" }),
        is_external: z.boolean(),
        is_multi_node_available: z.boolean(),
        multi_node_description: z.string().optional(),
        prices: ManagedServicesPricesBody,
        read_more_url: z.string().url(),
        service_name: ManagedServiceEnum,
        updated_at: updated_at.optional(),
        uuid,
        versions: z.array(z.string()),

        specification: z
            .object({
                provider: z.object({
                    workers: z.object({
                        packages: z.array(
                            z.object({
                                cpu: z.number(),
                                ram: z.number(),
                                ssd: z.number(),
                                default: z.array(z.enum(["win", "other"])),
                                disabled: z.array(z.enum(["win", "other"])).optional(),
                                os: z.array(z.enum(["win", "other"])),
                                notice: z.string().optional(),
                            }),
                        ),
                    }),
                }),
            })
            .optional(),
    })
    .openapi({ ref: "ServiceImage" });

export const ServiceImagesResponse = z.array(ServiceImage).openapi({ ref: "ServiceImagesResponse" });

export const ServiceSecrets = z
    .object({
        sql_pass: z.string().optional(),
        kubeconfig: z.string().optional(),
    })
    .openapi({ ref: "ServiceSecrets" });

export const ServiceWhiteList = z.array(IpWithSubnet).openapi({ ref: "ServiceWhiteList" });

export const ServiceUpdateBody = ManagedServiceBase.pick({
    display_name: true,
    billing_account_id: true,
})
    .partial()
    .openapi({ ref: "ServiceUpdateBody" });

export const ServiceWhitelistEntryBody = z
    .object({
        ip_address: IpWithSubnet,
    })
    .openapi({ ref: "ServiceWhitelistEntryBody" });

export const ServiceRebuildBody = z
    .object({
        replica_uuid,
        service_package_uuid: uuid,
    })
    .openapi({ ref: "ServiceRebuildBody" });

export const extraServicesSchemas = {
    ManagedDbService,
};

export const servicePaths = tagPaths("services")({
    "/user-resource/service/configurations": {
        get: {
            summary: "List managed services images",
            description:
                "Returns list of managed services images and corresponding versions for the platform Service section.",
            tags: ["parameters"],
            responses: {
                ...successResponse(ServiceImagesResponse),
            },
        },
    },

    "/user-resource/service/packages": {
        get: {
            summary: "List managed services",
            description: "Lists service packages of the given user.",
            responses: {
                ...successResponse(ServiceListResponse),
            },
        },
    },

    "/user-resource/service/package": {
        post: {
            summary: "Create managed service",
            description:
                "Creates a new managed service package. The package can be either single or multi node (availability depends on the service type)",
            requestBody: jsonBody(ManagedServiceCreateBody),
            responses: {
                ...successResponse(ManagedService),
            },
        },
    },
    "/user-resource/service/package/{uuid}": {
        patch: {
            summary: "Update managed service",
            description: "Updates service package display name and billing account",
            parameters: [params.uuid],
            requestBody: jsonBody(ServiceUpdateBody),
            responses: {
                ...successResponse(ManagedService),
            },
        },
        delete: {
            summary: "Delete managed service",
            description: "Deletes a service package data and resources",
            parameters: [params.uuid],
            responses: {
                ...successResponse(ManagedService),
            },
        },
    },

    "/user-resource/service/package/{uuid}/resources/{target_uuid}/start": {
        post: {
            summary: "Start managed service",
            parameters: [params.uuid, params.target_uuid],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },

    "/user-resource/service/package/{uuid}/resources/{target_uuid}/stop": {
        post: {
            summary: "Stop managed service",
            parameters: [params.uuid, params.target_uuid],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },

    "/{location}/user-resource/service/package/vm/rebuild": {
        post: {
            summary: "Rebuild managed service",
            parameters: [params.location],
            requestBody: jsonBody(ServiceRebuildBody),
            responses: {
                ...successResponse(ManagedService),
            },
        },
    },

    "/user-resource/service/package/{uuid}/secrets": {
        get: {
            summary: "Get secrets",
            description: "Returns the service secrets",
            parameters: [params.uuid],
            responses: {
                ...successResponse(ServiceSecrets),
            },
        },
    },
    "/user-resource/service/package/{uuid}/whitelist_addresses": {
        get: {
            summary: "Get whitelist addresses",
            description:
                "Whitelist prescribes a set of addresses that are allowed to make connections to Service host(s). An empty whitelist implies no restrictions, i.e. all source addresses are allowed.",
            parameters: [params.uuid],
            responses: {
                ...successResponse(ServiceWhiteList),
            },
        },
        post: {
            summary: "Add whitelist address",
            description:
                "Add address or subnet block entry to the whitelist of given Service. The response will reflect new state of the whitelist.",
            parameters: [params.uuid],
            requestBody: jsonBody(ServiceWhitelistEntryBody),
            responses: {
                ...successResponse(ServiceWhiteList),
            },
        },
        delete: {
            summary: "Delete whitelist address",
            description:
                "Delete an entry from whitelist of given Service. The response will reflect new state of the whitelist.",
            parameters: [params.uuid],
            requestBody: jsonBody(ServiceWhitelistEntryBody),
            responses: {
                ...successResponse(ServiceWhiteList),
            },
        },
    },
    // XXX: kubernetes maintenance settings
    "/user-resource/service/package/{uuid}/properties": {
        patch: {
            summary: `Update maintenance settings ${privateApi}`,
            parameters: [params.uuid],
            requestBody: jsonBody(KubernetesPropertiesBody),
            responses: {
                ...successResponse(ManagedService),
            },
        },
    },

    "/user-resource/service/package/{uuid}/action/hibernate": {
        post: {
            summary: `Hibernate/Awake Service ${privateApi}`,
            parameters: [params.uuid],
            requestBody: jsonBody(KubernetesHibernationBody),
            responses: {
                ...successResponse(ManagedService),
            },
        },
    },
});

// cspell:words Readyness
